import React, { useState, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "full", label: "Full Points" },
  { key: "partial", label: "Partial" },
  { key: "zero", label: "Zero" },
];

const classifyRow = (pts, max) => {
  if (max === 0) return "zero";
  if (pts >= max) return "full";
  if (pts <= 0) return "zero";
  return "partial";
};

// Maps scorecard keys (from the API's `scorecard.points` / `scorecard.max_points`)
// to a friendly component label and a status-string builder that reads from
// `scorecard.inputs`. Rows without a max_points entry are skipped, so the order
// here is the canonical display order.
const SCORE_ROWS = [
  {
    key: "trir",
    label: "Incident Rates",
    buildStatus: (i) =>
      i.trir_status ||
      (i.trir ? `TRIR Rate is ${i.trir}` : "—"),
  },
  {
    key: "fatalities",
    label: "Fatalities",
    buildStatus: (i) => {
      if (i.fatalities_status) return i.fatalities_status;
      const n = i.fatalities_last_3_years;
      if (n === "0" || n === 0) return "No Fatalities Reported in Last 3 Years";
      if (n != null && n !== "") return `${n} Fatalities Reported in Last 3 Years`;
      return "—";
    },
  },
  {
    key: "serious_incidents",
    label: "Serious Incidents",
    buildStatus: (i) => {
      if (i.serious_incidents_status) return i.serious_incidents_status;
      const n = i.serious_incidents_last_3_years;
      if (n === "0" || n === 0) return "No Serious or Life Altering Events Reported";
      if (n != null && n !== "") return `${n} Serious Incidents Reported`;
      return "—";
    },
  },
  {
    key: "safety_program",
    label: "Written Programs",
    buildStatus: (i) =>
      i.safety_program_status
        ? `Safety Program Status is ${i.safety_program_status}`
        : "—",
  },
  {
    key: "emr",
    label: "Experience Modifier Documents",
    buildStatus: (i) => (i.emr ? `Rate is ${i.emr}` : "—"),
  },
  {
    key: "medical_consortium",
    label: "Medical Case Management Provider",
    buildStatus: (i) =>
      i.medical_consortium_status
        ? `Medical Case Management Plan ${i.medical_consortium_status}`
        : "—",
  },
  {
    key: "drug_alcohol",
    label: "Drug & Alcohol Program",
    buildStatus: (i) =>
      i.drug_alcohol_status ? `D&A Status is ${i.drug_alcohol_status}` : "—",
  },
];

const gradeBadge = (grade) => {
  if (grade === "A") return "bg-green-100 text-green-800 border-green-200";
  if (grade === "B") return "bg-yellow-100 text-yellow-800 border-yellow-200";
  if (grade === "C") return "bg-red-100 text-red-800 border-red-200";
  return "bg-gray-100 text-gray-500 border-gray-200";
};

const approvalBadge = (status) => {
  if (status === "Approved") return "bg-green-600 text-white";
  if (status === "Conditional Approval") return "bg-yellow-500 text-white";
  if (status === "Not Approved") return "bg-red-600 text-white";
  return "bg-gray-300 text-gray-700";
};

const ScorecardPopup = ({ data, onClose }) => {
  const [activeFilter, setActiveFilter] = useState("all");

  const scorecard = data?.scorecard || {};
  const inputs = scorecard.inputs || {};
  const points = scorecard.points || {};
  const maxPoints = scorecard.max_points || {};

  const rows = SCORE_ROWS.filter((r) => r.key in maxPoints);

  // Count rows per bucket so filter chips can show counts and auto-disable empty buckets.
  const counts = useMemo(() => {
    const c = { all: rows.length, full: 0, partial: 0, zero: 0 };
    rows.forEach((r) => {
      c[classifyRow(points[r.key] ?? 0, maxPoints[r.key] ?? 0)]++;
    });
    return c;
  }, [rows, points, maxPoints]);

  if (!data) return null;

  const totalPoints = scorecard.total_score ?? rows.reduce((a, r) => a + (points[r.key] || 0), 0);
  const totalMax = rows.reduce((a, r) => a + (maxPoints[r.key] || 0), 0);

  const visibleRows =
    activeFilter === "all"
      ? rows
      : rows.filter(
          (r) =>
            classifyRow(points[r.key] ?? 0, maxPoints[r.key] ?? 0) === activeFilter
        );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] px-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Scorecard {data.firstname || data.lastname ? `— ${data.firstname || ""} ${data.lastname || ""}` : ""}
            </h3>
            {/* <p className="text-xs text-gray-500 mt-0.5">
              Auto-computed from Previous Three-Year HSE Statistics and Safety answers.
            </p> */}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <CloseIcon />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {/* Summary row */}
          <div className="flex items-center justify-between mb-5 p-4 rounded-lg bg-gray-50 border">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Total Score</div>
              <div className="text-2xl font-bold">
                {totalPoints}/{totalMax || 100}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Grade</div>
              <span
                className={`inline-block px-3 py-1 text-sm font-bold rounded-full border ${gradeBadge(
                  scorecard.grade
                )}`}
              >
                {scorecard.grade || "—"}
              </span>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Approval</div>
              <span
                className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${approvalBadge(
                  scorecard.approval_status
                )}`}
              >
                {scorecard.approval_status || "—"}
              </span>
            </div>
          </div>

          {/* Filter chips */}
          <div className="flex flex-wrap gap-2 mb-3">
            {FILTERS.map((f) => {
              const count = counts[f.key] ?? 0;
              const active = activeFilter === f.key;
              const disabled = count === 0 && f.key !== "all";
              return (
                <button
                  key={f.key}
                  onClick={() => !disabled && setActiveFilter(f.key)}
                  disabled={disabled}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    active
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  {f.label} ({count})
                </button>
              );
            })}
          </div>

          {/* Component breakdown table */}
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left font-semibold text-gray-700 px-4 py-3">
                  Scorecard Component
                </th>
                <th className="text-left font-semibold text-gray-700 px-4 py-3">
                  Status
                </th>
                <th className="text-right font-semibold text-gray-700 px-4 py-3">
                  Points
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-6 text-center text-sm text-gray-500">
                    No components match this filter.
                  </td>
                </tr>
              )}
              {visibleRows.map((row) => {
                const p = points[row.key] ?? 0;
                const mx = maxPoints[row.key] ?? 0;
                return (
                  <tr key={row.key} className="border-b last:border-b-0">
                    <td className="px-4 py-3 text-left">
                      <span className="text-blue-700 underline decoration-blue-300 underline-offset-2 cursor-default">
                        {row.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-800 text-left">
                      {row.buildStatus(inputs)}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-700 font-medium tabular-nums">
                      {p} / {mx}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-gray-300">
                <td className="px-4 py-3" />
                <td className="px-4 py-3 text-right font-semibold text-gray-700">
                  Total Points:
                </td>
                <td className="px-4 py-3 text-right font-bold text-gray-900 tabular-nums">
                  {totalPoints} / {totalMax || 100}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ScorecardPopup;
